/**
 * AI Assurance 360° — ECharts options
 */
import type { EChartsCoreOption } from 'echarts/core';
import { BANDS } from './aiAssurance.utils';
import type { AiAssuranceMetrics } from './aiAssurance.utils';
import { RISK_LEVELS } from './aiAssurance.utils';

const LENS_LABELS = ['Ethics', 'Compliance', 'Governance', 'Safety', 'Security', 'Trust'];

const BAND_COLORS: Record<string, string> = {
  EARLY: '#f44336',
  EMERGING: '#ff9800',
  DEFINED: '#ffc107',
  ADVANCED: '#8bc34a',
  LEADING: '#4caf50'
};

export function getScorecardGaugeOption(metrics: AiAssuranceMetrics): EChartsCoreOption {
  const current = metrics.maturity_index;
  const target = metrics.target;
  const bandLabel = metrics.maturity_level;
  const pctToTarget = Math.round((current / target) * 100);

  const stackedValues = BANDS.map(([lo, hi]) => hi - lo);

  const seriesBands = BANDS.map((_, i) => ({
    type: 'bar' as const,
    stack: 'gauge',
    barWidth: 56,
    data: [stackedValues[i]],
    itemStyle: {
      color: BAND_COLORS[BANDS[i]![2]] ?? '#ddd',
      borderRadius: i === 0 ? [4, 0, 0, 4] : i === BANDS.length - 1 ? [0, 4, 4, 0] : 0
    },
    emphasis: { disabled: true },
    silent: true,
    z: 1
  }));

  return {
    title: {
      text: 'AI Assurance Maturity',
      subtext: `Band: ${bandLabel}   |   ${pctToTarget}% of target reached`,
      left: 'center',
      top: 8,
      textStyle: { fontSize: 15, fontWeight: 'bold', color: '#212529' },
      subtextStyle: { fontSize: 12, color: '#6c757d' }
    },
    tooltip: {
      trigger: 'axis',
      formatter: () =>
        `<div style="padding:6px 10px;line-height:2">
          <b>Current Score:</b> ${current} / 100<br/>
          <b>Target:</b> ${target}<br/>
          <b>Band:</b> ${bandLabel}<br/>
          <b>Progress to target:</b> ${pctToTarget}%
        </div>`
    },
    grid: {
      left: 20,
      right: 20,
      top: 100,
      bottom: 80,
      containLabel: true
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: 100,
      interval: 5,
      axisLine: { show: true, lineStyle: { color: '#dee2e6' } },
      axisTick: {
        show: true,
        length: 4,
        lineStyle: { color: '#adb5bd' }
      },
      axisLabel: {
        show: true,
        fontSize: 11,
        color: '#6c757d',
        margin: 12,
        rotate: 0,
        formatter: (val: number) => {
          if (val === 0) return '0\nEARLY';
          if (val === 40) return '40\nEMERGING';
          if (val === 60) return '60\nDEFINED';
          if (val === 75) return '75\nADVANCED';
          if (val === 90) return '90\nLEADING';
          if (val === 100) return '100';
          return '';
        }
      },
      splitLine: {
        show: true,
        lineStyle: { color: '#f0f0f0', type: 'dashed' }
      }
    },
    yAxis: {
      type: 'category',
      data: [''],
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false }
    },
    graphic: [
      // ── Current score label — floats ABOVE the bar ──────────────────
      {
        type: 'group',
        z: 100,
        children: [
          {
            type: 'rect',
            shape: { x: -36, y: -52, width: 72, height: 24, r: 4 },
            style: { fill: '#212529' }
          },
          {
            type: 'text',
            style: {
              text: `Score: ${current}`,
              fill: '#ffffff',
              fontSize: 12,
              fontWeight: 'bold',
              textAlign: 'center',
              textVerticalAlign: 'middle',
              x: 0,
              y: -40
            }
          },
          // downward arrow
          {
            type: 'line',
            shape: { x1: 0, y1: -28, x2: 0, y2: -10 },
            style: { stroke: '#212529', lineWidth: 2 }
          }
        ],
        // position will be set via markLine — this is a fallback graphic
        invisible: true
      },
      // ── Target label — floats BELOW the bar ────────────────────────
      {
        type: 'group',
        z: 99,
        children: [
          {
            type: 'rect',
            shape: { x: -36, y: 10, width: 72, height: 24, r: 4 },
            style: { fill: '#495057' }
          },
          {
            type: 'text',
            style: {
              text: `Target: ${target}`,
              fill: '#ffffff',
              fontSize: 11,
              textAlign: 'center',
              textVerticalAlign: 'middle',
              x: 0,
              y: 22
            }
          }
        ],
        invisible: true
      }
    ],
    series: [
      // ── Colored band bars ──────────────────────────────────────────
      ...seriesBands,

      // ── Current score needle ───────────────────────────────────────
      {
        type: 'bar' as const,
        barWidth: 36,
        barGap: '-100%',
        data: [0],
        itemStyle: { color: 'transparent' },
        silent: true,
        z: 10,
        markLine: {
          symbol: ['none', 'none'],
          animation: false,
          silent: true,
          data: [
            {
              xAxis: current,
              lineStyle: { color: '#212529', width: 3, type: 'solid' },
              label: {
                show: true,
                position: 'end',
                distance: 6,
                formatter: `Score: ${current}`,
                color: '#212529',
                backgroundColor: '#ffffff',
                borderColor: '#212529',
                borderWidth: 1.5,
                borderRadius: 4,
                padding: [4, 8],
                fontSize: 12,
                fontWeight: 'bold',
                rotate: 0
              }
            }
          ]
        }
      },

      // ── Target needle ──────────────────────────────────────────────
      {
        type: 'bar' as const,
        barWidth: 36,
        barGap: '-100%',
        data: [0],
        itemStyle: { color: 'transparent' },
        silent: true,
        z: 9,
        markLine: {
          symbol: ['none', 'none'],
          animation: false,
          silent: true,
          data: [
            {
              xAxis: target,
              lineStyle: { color: '#495057', width: 2, type: 'dashed' },
              label: {
                show: true,
                position: 'end',
                distance: 6,
                formatter: `Target: ${target}`,
                color: '#495057',
                backgroundColor: '#ffffff',
                borderColor: '#495057',
                borderWidth: 1.5,
                borderRadius: 4,
                padding: [4, 8],
                fontSize: 11,
                rotate: 0
              }
            }
          ]
        }
      }
    ]
  };
}

export function getRadarOption(metrics: AiAssuranceMetrics, targetMaturity: number): EChartsCoreOption {
  const currentVals = LENS_LABELS.map(k => metrics.lens_scores[k] ?? 0);
  const targetVals = LENS_LABELS.map(() => targetMaturity);
  const indicator = LENS_LABELS.map(name => ({ name, max: 100 }));

  return {
    title: {
      text: `360° AI Assurance Lens\n(Where the polygon dips = enterprise weakness)`,
      left: 'center',
      top: 0,
      textStyle: { fontSize: 13 }
    },
    tooltip: { trigger: 'item' },
    legend: { data: ['Current', `Target (${targetMaturity})`], bottom: 0 },
    radar: {
      indicator,
      center: ['50%', '52%'],
      radius: '60%',
      axisName: { color: '#333' },
      splitArea: { areaStyle: { color: ['rgba(0,0,0,0.03)', 'rgba(0,0,0,0.05)'] } },
      splitLine: { lineStyle: { color: 'rgba(0,0,0,0.15)' } },
      axisLine: { lineStyle: { color: 'rgba(0,0,0,0.2)' } }
    },
    series: [
      {
        type: 'radar',
        data: [
          { value: currentVals, name: 'Current', areaStyle: { opacity: 0.2 }, lineStyle: { width: 2 } },
          {
            value: targetVals,
            name: `Target (${targetMaturity})`,
            lineStyle: { type: 'dashed', width: 2 },
            areaStyle: { opacity: 0.05 }
          }
        ]
      }
    ]
  };
}

export function getHeatmapOption(dimLabels: string[], mat: number[][]): EChartsCoreOption {
  const flatData: [number, number, number][] = [];
  for (let i = 0; i < dimLabels.length; i++) {
    for (let j = 0; j < RISK_LEVELS.length; j++) {
      flatData.push([j, i, mat[i]![j]!]);
    }
  }
  const maxCount = Math.max(1, ...flatData.map(d => d[2]));

  return {
    title: {
      text: 'Ethical & Security Risk Concentration\n(Counts of review events by risk level)',
      left: 'center',
      top: 0,
      textStyle: { fontSize: 13 }
    },
    tooltip: {
      position: 'top',
      formatter: (p: unknown) => {
        const params = p as { data: [number, number, number] };
        const [x, y, v] = params.data;
        return `${dimLabels[y]!} — ${RISK_LEVELS[x]!}: ${v}`;
      }
    },
    grid: { left: '12%', right: '8%', top: '18%', bottom: '12%', containLabel: true },
    xAxis: {
      type: 'category',
      data: [...RISK_LEVELS],
      splitArea: { show: false },
      axisLabel: { color: '#333' }
    },
    yAxis: {
      type: 'category',
      data: dimLabels,
      splitArea: { show: false },
      axisLabel: { color: '#333' }
    },
    visualMap: {
      min: 0,
      max: maxCount,
      calculable: true,
      orient: 'horizontal',
      left: 'center',
      bottom: 0,
      inRange: { color: ['#e3f2fd', '#1976d2', '#0d47a1'] },
      textStyle: { fontSize: 11 }
    },
    series: [
      {
        type: 'heatmap',
        data: flatData,
        label: { show: true, formatter: (p: { data: [number, number, number] }) => String(p.data[2]) },
        emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0,0,0,0.3)' } }
      }
    ]
  };
}

// ── Fixed: added not_approved as 5th param ────────────────────────────────
export function getGovernanceTrendOption(
  dates: string[],
  approved: number[],
  conditional: number[],
  rejected: number[],
  not_approved: number[] = []
): EChartsCoreOption {
  return {
    title: {
      text: 'Governance Stability Trend\n(Is the approval process stable over time?)',
      left: 'center',
      top: 0,
      textStyle: { fontSize: 13 }
    },
    tooltip: { trigger: 'axis' },
    legend: { data: ['Approved', 'Conditional', 'Rejected', 'Not Approved'], bottom: 0 },
    grid: { left: '8%', right: '6%', bottom: '22%', top: '18%', containLabel: true },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: { color: '#333', rotate: dates.length > 10 ? 45 : 0 }
    },
    yAxis: {
      type: 'value',
      name: 'Number of Review Events',
      axisLabel: { color: '#333' },
      splitLine: { lineStyle: { color: '#eee' } }
    },
    series: [
      { type: 'line', name: 'Approved', data: approved, symbol: 'circle', symbolSize: 8, lineStyle: { width: 2 } },
      {
        type: 'line',
        name: 'Conditional',
        data: conditional,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: { width: 2 }
      },
      { type: 'line', name: 'Rejected', data: rejected, symbol: 'circle', symbolSize: 8, lineStyle: { width: 2 } },
      {
        type: 'line',
        name: 'Not Approved',
        data: not_approved,
        symbol: 'circle',
        symbolSize: 8,
        lineStyle: { width: 2, type: 'dashed' }
      }
    ]
  };
}
